import type { SearchTableQuery } from "owa-mail-list-search";
import type { PerformanceDatapoint } from "owa-analytics";
import type { PerformanceDatapointType } from "owa-analytics-types";
import type { MailListRowDataType, TableQuery } from "owa-mail-list-store";
import type { ActionSource } from "owa-mail-store";
import type { MessageSuggestion, SearchResults, SearchScope } from "owa-search-service";
import { action } from "satcheljs";
import type { MailboxInfo } from "owa-client-types";

export interface LoadTableViewFromSearchTableQueryActionMessage {
    searchTableQuery: TableQuery;
    searchEndToEndPerformanceDatapoint: PerformanceDatapoint;
    actionSource: ActionSource;
    continuedSearchEndToEndDatapoint?: PerformanceDatapointType;
}

/**
 * Dispatched to clear current search scope (i.e. change it to broadest scope).
 * Action message provides option to kick off search after scope is changed.
 *
 * Currently, this action is only used in MailListEmptyViewState component.
 */
export interface CLEAR_SEARCH_SCOPEActionMessage {
    actionSource: string;
    shouldStartSearch: boolean;
}
export const clearSearchScope: (actionSource: string, shouldStartSearch: boolean) => CLEAR_SEARCH_SCOPEActionMessage = action("CLEAR_SEARCH_SCOPE", (actionSource: string, shouldStartSearch: boolean): CLEAR_SEARCH_SCOPEActionMessage => ({
    actionSource,
    shouldStartSearch,
}));

export interface LOAD_MESSAGE_SUGGESTION_INTO_TABLEActionMessage {
    actionSource: string;
    row: MailListRowDataType;
    searchTableQuery: SearchTableQuery;
    referenceId: string;
    traceId: string;
    itemPartId: string | undefined;
}
export const loadMessageSuggestionIntoTable: (actionSource: string, row: MailListRowDataType, searchTableQuery: SearchTableQuery, referenceId: string, traceId: string, itemPartId?: string) => LOAD_MESSAGE_SUGGESTION_INTO_TABLEActionMessage = action("LOAD_MESSAGE_SUGGESTION_INTO_TABLE", (actionSource: string, row: MailListRowDataType, searchTableQuery: SearchTableQuery, referenceId: string, traceId: string, itemPartId?: string): LOAD_MESSAGE_SUGGESTION_INTO_TABLEActionMessage => ({
    actionSource,
    row,
    searchTableQuery,
    referenceId,
    traceId,
    itemPartId,
}));

export interface QUERY_GENWEB_ON_TYPEActionMessage {
    queryString: string;
    requestId: string;
    mailboxInfo: MailboxInfo;
    searchSessionGuid: string;
    searchScope: SearchScope;
    queryOnTypeSearchNumber: number;
}
export const queryGenWebOnType: (queryString: string, requestId: string, mailboxInfo: MailboxInfo, searchSessionGuid: string, searchScope: SearchScope, queryOnTypeSearchNumber: number) => QUERY_GENWEB_ON_TYPEActionMessage = action("QUERY_GENWEB_ON_TYPE", (queryString: string, requestId: string, mailboxInfo: MailboxInfo, searchSessionGuid: string, searchScope: SearchScope, queryOnTypeSearchNumber: number): QUERY_GENWEB_ON_TYPEActionMessage => ({
    queryString,
    requestId,
    mailboxInfo,
    searchSessionGuid,
    searchScope,
    queryOnTypeSearchNumber,
}));

export interface LOAD_GENWEB_QUERY_ON_TYPE_RESULTSActionMessage {
    searchTableQuery: SearchTableQuery;
    searchResults: SearchResults;
    messageSuggestions: MessageSuggestion[];
    requestId: string;
    mailboxInfo: MailboxInfo;
    searchSessionGuid: string;
    queryOnTypeSearchNumber: number;
}
export const loadGenWebQueryOnTypeResults: (searchTableQuery: SearchTableQuery, searchResults: SearchResults, messageSuggestions: MessageSuggestion[], requestId: string, mailboxInfo: MailboxInfo, searchSessionGuid: string, queryOnTypeSearchNumber: number) => LOAD_GENWEB_QUERY_ON_TYPE_RESULTSActionMessage = action("LOAD_GENWEB_QUERY_ON_TYPE_RESULTS", (searchTableQuery: SearchTableQuery, searchResults: SearchResults, messageSuggestions: MessageSuggestion[], requestId: string, mailboxInfo: MailboxInfo, searchSessionGuid: string, queryOnTypeSearchNumber: number): LOAD_GENWEB_QUERY_ON_TYPE_RESULTSActionMessage => ({
    searchTableQuery,
    searchResults,
    messageSuggestions,
    requestId,
    mailboxInfo,
    searchSessionGuid,
    queryOnTypeSearchNumber,
}));

export interface LOAD_TABLE_VIEW_FROM_SEARCH_TABLE_QUERYActionMessage {
    searchTableQuery: TableQuery;
    searchEndToEndPerformanceDatapoint: PerformanceDatapoint;
    actionSource: ActionSource;
    continuedSearchEndToEndDatapoint?: PerformanceDatapointType;
}
export const loadTableViewFromSearchTableQuery: (searchTableQuery: TableQuery, searchEndToEndPerformanceDatapoint: PerformanceDatapoint, actionSource: ActionSource, continuedSearchEndToEndDatapoint?: PerformanceDatapointType) => LOAD_TABLE_VIEW_FROM_SEARCH_TABLE_QUERYActionMessage = action("LOAD_TABLE_VIEW_FROM_SEARCH_TABLE_QUERY", (searchTableQuery: TableQuery, searchEndToEndPerformanceDatapoint: PerformanceDatapoint, actionSource: ActionSource, continuedSearchEndToEndDatapoint?: PerformanceDatapointType): LOAD_TABLE_VIEW_FROM_SEARCH_TABLE_QUERYActionMessage => ({
    searchTableQuery,
    searchEndToEndPerformanceDatapoint,
    actionSource,
    continuedSearchEndToEndDatapoint,
}));

export interface SET_STATIC_SEARCH_SCOPEActionMessage {
    searchScope: SearchScope;
}
export const setStaticSearchScope: (searchScope: SearchScope) => SET_STATIC_SEARCH_SCOPEActionMessage = action("SET_STATIC_SEARCH_SCOPE", (searchScope: SearchScope): SET_STATIC_SEARCH_SCOPEActionMessage => ({
    searchScope,
}));

export interface OnFolderScopeUpdateActionMessage {
    selectedSearchScope: SearchScope;
}
export const onFolderScopeUpdate: (selectedSearchScope: SearchScope) => OnFolderScopeUpdateActionMessage = action("onFolderScopeUpdate", (selectedSearchScope: SearchScope): OnFolderScopeUpdateActionMessage => ({
    selectedSearchScope,
}));

export interface FIND_EMAIL_FROM_SENDER_CLICKEDActionMessage {
    senderName: string;
    senderEmail: string;
    actionSource: ActionSource;
    mailboxInfo: MailboxInfo;
    entryPoint: string | undefined;
}
export const findEmailFromSender: (senderName: string, senderEmail: string, actionSource: ActionSource, mailboxInfo: MailboxInfo, entryPoint?: string) => FIND_EMAIL_FROM_SENDER_CLICKEDActionMessage = action("FIND_EMAIL_FROM_SENDER_CLICKED", (senderName: string, senderEmail: string, actionSource: ActionSource, mailboxInfo: MailboxInfo, entryPoint?: string): FIND_EMAIL_FROM_SENDER_CLICKEDActionMessage => ({
    senderName,
    senderEmail,
    actionSource,
    mailboxInfo,
    entryPoint,
}));

export interface FIND_EMAIL_FROM_CONVERSATION_CLICKEDActionMessage {
    subjectText: string;
    currentMailbox: MailboxInfo;
    entryPoint: string;
}
export const findEmailFromConversation: (subjectText: string, currentMailbox: MailboxInfo, entryPoint: string) => FIND_EMAIL_FROM_CONVERSATION_CLICKEDActionMessage = action("FIND_EMAIL_FROM_CONVERSATION_CLICKED", (subjectText: string, currentMailbox: MailboxInfo, entryPoint: string): FIND_EMAIL_FROM_CONVERSATION_CLICKEDActionMessage => ({
    subjectText,
    currentMailbox,
    entryPoint,
}));
